import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardHeader, CardTitle, CardContent, CardDescription, CardFooter } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useToast } from "@/components/ui/use-toast"
import { Loader2, Lightbulb, Users, TrendingUp } from "lucide-react"
import { LoadingDialog } from "@/components/ui/loading-dialog"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import ReactMarkdown from "react-markdown"
import { formatDistanceToNow, isValid, parseISO } from "date-fns"

// Types for brainstorming categories
type BrainstormCategory = 'innovation' | 'collaboration' | 'growth'
type BrainstormTopic = {
  id: string
  name: string
  description: string
  prompts: string[]
}

interface TrendCard {
  title: string
  description: string
  category: string
}

interface NewsArticle {
  title: string
  summary: string
  source?: string
  url?: string
  createdAt: string
}

interface BrainstormSettings {
  category: BrainstormCategory
  topic: string
  prompt: string
  customPrompt?: string
}

const brainstormTopics: Record<BrainstormCategory, BrainstormTopic[]> = {
  innovation: [
    {
      id: "digital-transformation",
      name: "Digital Transformation",
      description: "Explore ways to leverage technology and AI for greater impact",
      prompts: [
        "How can we use AI to improve our service delivery?",
        "What digital tools could help us reach more beneficiaries?",
        "How can we measure and communicate our impact better using data?",
        "What innovative fundraising methods could we explore?"
      ]
    },
    {
      id: "program-innovation",
      name: "Program Innovation",
      description: "Develop new or improved programs and services",
      prompts: [
        "How can we adapt our programs to current community needs?",
        "What new services could we offer to increase impact?",
        "How can we make our programs more accessible?",
        "What emerging community needs should we address?"
      ]
    }
  ],
  collaboration: [
    {
      id: "partnerships",
      name: "Strategic Partnerships",
      description: "Identify and develop impactful partnerships",
      prompts: [
        "What organizations could we partner with to increase impact?",
        "How can we create win-win collaborations?",
        "What resources could we share with other nonprofits?",
        "How can we engage corporate partners more effectively?"
      ]
    },
    {
      id: "community-engagement",
      name: "Community Engagement",
      description: "Strengthen community relationships and involvement",
      prompts: [
        "How can we better engage our local community?",
        "What volunteer opportunities could we create?",
        "How can we amplify community voices in our work?",
        "What community assets could we leverage?"
      ]
    }
  ],
  growth: [
    {
      id: "sustainable-funding",
      name: "Sustainable Funding",
      description: "Develop diverse and sustainable funding streams",
      prompts: [
        "How can we diversify our revenue streams?",
        "What new fundraising channels should we explore?",
        "How can we better engage and retain donors?",
        "What earned income opportunities exist?"
      ]
    },
    {
      id: "capacity-building",
      name: "Capacity Building",
      description: "Strengthen organizational capacity and efficiency",
      prompts: [
        "How can we optimize our resources and operations?",
        "What skills do we need to develop in our team?",
        "How can we improve our internal processes?",
        "What infrastructure investments should we prioritize?"
      ]
    }
  ]
}

const categoryColors: Record<string, { bg: string; text: string }> = {
  'Local Impact': { bg: 'bg-blue-100', text: 'text-blue-700' },
  'Policy & Funding': { bg: 'bg-green-100', text: 'text-green-700' },
  'Community Engagement': { bg: 'bg-purple-100', text: 'text-purple-700' },
  'default': { bg: 'bg-gray-100', text: 'text-gray-700' }
}

// Helper function to safely format date
const formatTimeAgo = (dateString: string) => {
  try {
    // First try to parse as ISO date
    const date = parseISO(dateString)
    if (!isValid(date)) {
      // If not valid ISO, try as regular date
      const fallbackDate = new Date(dateString)
      if (!isValid(fallbackDate)) return null
      return formatDistanceToNow(fallbackDate, { addSuffix: true })
    }
    return formatDistanceToNow(date, { addSuffix: true })
  } catch (error) {
    console.error('Invalid date:', dateString)
    return null
  }
}

export function BrainstormIdeasPage({ profile, session }: { profile: any | null, session: any | null }) {
  const { toast } = useToast()
  const [insights, setInsights] = useState<Array<{
    title: string;
    summary: string;
    source?: string;
    url?: string;
    createdAt: string;
  }>>([])
  const [isLoading, setIsLoading] = useState(false)
  const [settings, setSettings] = useState<BrainstormSettings>({
    category: 'innovation',
    topic: '',
    prompt: '',
  })
  const [generatedIdeas, setGeneratedIdeas] = useState<string>('')
  
  // Cache helpers
  const CACHE_KEY = 'npo_insights_cache'
  const CACHE_TTL = 12 * 60 * 60 * 1000 // 12 hours in milliseconds

  const getFromCache = () => {
    try {
      const cached = localStorage.getItem(CACHE_KEY)
      if (!cached) return null

      const { data, timestamp } = JSON.parse(cached)
      const now = Date.now()

      // Check if cache is still valid
      if (now - timestamp > CACHE_TTL) {
        localStorage.removeItem(CACHE_KEY)
        return null
      }

      // Sort cached data by date
      return data.sort((a: any, b: any) => {
        const dateA = new Date(a.createdAt)
        const dateB = new Date(b.createdAt)
        return dateB.getTime() - dateA.getTime() // Newest first
      })
    } catch (error) {
      console.error('Cache read error:', error)
      return null
    }
  }

  const setCache = (data: any) => {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({
        data,
        timestamp: Date.now()
      }))
    } catch (error) {
      console.error('Cache write error:', error)
    }
  }

  useEffect(() => {
    const fetchInitialData = async () => {
      if (!session?.access_token) return
      
      try {
        setIsLoading(true)

        // Try to get from cache first
        const cachedData = getFromCache()
        if (cachedData) {
          setInsights(cachedData)
          setIsLoading(false)
          return
        }

        // If no cache, fetch fresh data
        const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/tools/insights?o=npo`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          }
        })

        if (!response.ok) {
          throw new Error('Failed to fetch NPO insights')
        }

        const { data } = await response.json()
        setInsights(data)
        // Cache the fresh data
        setCache(data)
      } catch (error) {
        console.error('Error fetching insights:', error)
        toast({
          title: "Error",
          description: "Could not fetch insights. Please try refreshing the page.",
          variant: "destructive",
        })
      } finally {
        setIsLoading(false)
      }
    }

    fetchInitialData()
  }, [session?.access_token]) // Only re-run if access token changes

  // Determine category based on title
  const getCategoryFromTitle = (title: string) => {
    const lowerTitle = title.toLowerCase()
    if (lowerTitle.includes('local impact')) return 'Local Impact'
    if (lowerTitle.includes('policy') || lowerTitle.includes('funding')) return 'Policy & Funding'
    if (lowerTitle.includes('community') || lowerTitle.includes('program')) return 'Community Engagement'
    return 'Other'
  }

  // Handle settings changes
  const handleCategoryChange = (category: BrainstormCategory) => {
    setSettings({
      ...settings,
      category,
      topic: '',
      prompt: ''
    })
  }

  const handleTopicChange = (topicId: string) => {
    setSettings({
      ...settings,
      topic: topicId,
      prompt: ''
    })
  }

  const handlePromptChange = (prompt: string) => {
    setSettings({
      ...settings,
      prompt
    })
  }

  const handleCustomPromptChange = (customPrompt: string) => {
    setSettings({
      ...settings,
      customPrompt
    })
  }

  // Generate ideas using AI
  const generateIdeas = async () => {
    try {
      setIsLoading(true)
      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/tools/brainstorm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          npoId: session.user.id,
          focusArea: settings.category,
          targetAudience: '',
          duration: '',
          format: '',
          resourcesAvailable: '',
          successMetrics: '',
          constraints: '',
          npoInsights: insights
        })
      })

      if (!response.ok) {
        throw new Error('Failed to generate ideas')
      }

      const { data } = await response.json()
      setGeneratedIdeas(data.ideas)
    } catch (error) {
      console.error('Error generating ideas:', error)
      toast({
        title: "Error",
        description: "Could not generate ideas. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  const selectedTopic = settings.topic 
    ? brainstormTopics[settings.category].find(t => t.id === settings.topic)
    : null

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Brainstorm Ideas</h1>
      </div>

      <Tabs defaultValue="trends" className="space-y-6">
        <TabsList>
          <TabsTrigger value="trends">Trends & News</TabsTrigger>
          <TabsTrigger value="brainstorm">Brainstorm</TabsTrigger>
        </TabsList>

        <TabsContent value="trends" className="space-y-6">
          {isLoading ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">
                Loading local nonprofit insights...
              </p>
              <Loader2 className="w-6 h-6 animate-spin mx-auto" />
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">Local Nonprofit News & Trends</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {insights.map((insight, index) => (
                  <a 
                    key={index}
                    href={insight.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block group"
                  >
                    <Card className="h-full hover:shadow-lg transition-shadow duration-200">
                      <CardHeader className="p-4 pb-2">
                        <CardTitle className="text-base font-medium line-clamp-2 text-slate-900 group-hover:underline decoration-from-font dark:text-slate-100">{insight.title}</CardTitle>
                      </CardHeader>
                      <CardContent className="p-4 pt-0">
                        <p className="text-sm text-slate-600 line-clamp-3 dark:text-slate-300">{insight.summary}</p>
                        <div className="mt-2 flex items-center justify-between text-xs">
                          <span className="text-slate-500 dark:text-slate-400">{insight.source}</span>
                          <time 
                            dateTime={insight.createdAt}
                            className="text-slate-400 dark:text-slate-500"
                          >
                            {formatTimeAgo(insight.createdAt) || 'Recently'}
                          </time>
                        </div>
                      </CardContent>
                    </Card>
                  </a>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="brainstorm" className="space-y-6">
          <Card className="p-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <div className="grid grid-cols-3 gap-4">
                  <Button
                    variant={settings.category === 'innovation' ? 'default' : 'outline'}
                    onClick={() => handleCategoryChange('innovation')}
                    className="w-full"
                  >
                    <Lightbulb className="w-4 h-4 mr-2" />
                    Innovation
                  </Button>
                  <Button
                    variant={settings.category === 'collaboration' ? 'default' : 'outline'}
                    onClick={() => handleCategoryChange('collaboration')}
                    className="w-full"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Collaboration
                  </Button>
                  <Button
                    variant={settings.category === 'growth' ? 'default' : 'outline'}
                    onClick={() => handleCategoryChange('growth')}
                    className="w-full"
                  >
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Growth
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Topic</Label>
                <Select
                  value={settings.topic}
                  onValueChange={handleTopicChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a topic" />
                  </SelectTrigger>
                  <SelectContent>
                    {brainstormTopics[settings.category].map((topic) => (
                      <SelectItem key={topic.id} value={topic.id}>
                        {topic.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedTopic && (
                <div className="space-y-2">
                  <Label>Prompt</Label>
                  <Select
                    value={settings.prompt}
                    onValueChange={handlePromptChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a prompt or write your own" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedTopic.prompts.map((prompt, index) => (
                        <SelectItem key={index} value={prompt}>
                          {prompt}
                        </SelectItem>
                      ))}
                      <SelectItem value="custom">Write your own prompt</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {settings.prompt === 'custom' && (
                <div className="space-y-2">
                  <Label>Custom Prompt</Label>
                  <Textarea
                    placeholder="Enter your brainstorming prompt..."
                    value={settings.customPrompt}
                    onChange={(e) => handleCustomPromptChange(e.target.value)}
                  />
                </div>
              )}

              <Button
                onClick={generateIdeas}
                disabled={isLoading || !settings.topic || (!settings.prompt && !settings.customPrompt)}
                className="w-full"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating Ideas...
                  </>
                ) : (
                  'Generate Ideas'
                )}
              </Button>
            </div>
          </Card>

          {generatedIdeas && (
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Generated Ideas</h2>
              <div className="prose prose-sm max-w-none [&>h2]:text-lg [&>h2]:font-semibold [&>h2]:mt-6 [&>h2]:mb-2 [&>ul]:mt-2 [&>ul]:mb-4 [&>ul>li]:mt-1 [&>p]:my-2 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                <ReactMarkdown>{generatedIdeas}</ReactMarkdown>
              </div>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      <LoadingDialog
        isOpen={isLoading}
        message="Generating creative ideas and gathering relevant insights..."
      />
    </div>
  )
}

export default BrainstormIdeasPage
